import { defineStore } from "pinia";
import { isGroupComplete } from "@/config/sellerProfile/completeness";
import { SELLER_PROFILE_GROUP } from "@/config/sellerProfile/groups";
import { emptyPaymentDelivery } from "@/config/sellerProfile/paymentDeliveryCompleteness";
import { PAYMENT_DELIVERY_SECTIONS } from "@/config/sellerProfile/paymentDeliverySections";
import { emptyStoreInfo } from "@/config/sellerProfile/storeInfoCompleteness";
import { STORE_INFO_SECTIONS } from "@/config/sellerProfile/storeInfoSections";
import {
    getCompleteness,
    getPaymentDelivery,
    getStoreInfo,
    updateSection as persistPaymentDeliverySection,
    updateStoreInfoSection as persistStoreInfoSection,
} from "@/services/sellerProfile/SellerProfileService";
import { useShopStore } from "@/store/shop";

export const useSellerProfileStore = defineStore("sellerProfile", {
    state: () => ({
        shopId: null,
        loading: false,
        paymentDelivery: emptyPaymentDelivery(),
        paymentDeliveryComplete: false,
        storeInfo: emptyStoreInfo(),
        storeInfoComplete: false,
        completenessReady: false,
    }),
    actions: {
        resetCompleteness() {
            this.paymentDeliveryComplete = false;
            this.storeInfoComplete = false;
            this.completenessReady = false;
        },
        syncPaymentDeliveryComplete() {
            this.paymentDeliveryComplete = isGroupComplete(this.paymentDelivery, PAYMENT_DELIVERY_SECTIONS);
        },
        syncStoreInfoComplete() {
            this.storeInfoComplete = isGroupComplete(this.storeInfo, STORE_INFO_SECTIONS);
        },
        async loadCompleteness(shopId) {
            const nextShopId = shopId || null;

            if (!nextShopId) {
                this.shopId = null;
                this.resetCompleteness();
                return;
            }

            if (this.shopId === nextShopId && this.completenessReady) {
                return;
            }

            this.shopId = nextShopId;
            this.resetCompleteness();

            try {
                const data = await getCompleteness(nextShopId);

                if (this.shopId !== nextShopId) {
                    return;
                }

                this.paymentDeliveryComplete = Boolean(data.paymentDeliveryComplete);
                this.storeInfoComplete = Boolean(data.storeInfoComplete);
            } catch {
                if (this.shopId !== nextShopId) {
                    return;
                }

                this.paymentDeliveryComplete = false;
                this.storeInfoComplete = false;
            } finally {
                if (this.shopId === nextShopId) {
                    this.completenessReady = true;
                }
            }
        },
        async loadPaymentDelivery(shopId) {
            const nextShopId = shopId || null;

            if (this.shopId !== nextShopId) {
                this.resetCompleteness();
            }

            this.shopId = nextShopId;
            this.paymentDelivery = emptyPaymentDelivery();

            if (!nextShopId) {
                this.loading = false;
                return;
            }

            this.loading = true;
            const requestedShopId = nextShopId;

            try {
                const profile = await getPaymentDelivery(requestedShopId);

                if (this.shopId !== requestedShopId) {
                    return;
                }

                this.paymentDelivery = profile;
                this.syncPaymentDeliveryComplete();
                this.completenessReady = true;
            } catch {
                if (this.shopId !== requestedShopId) {
                    return;
                }

                this.paymentDelivery = emptyPaymentDelivery();
                this.syncPaymentDeliveryComplete();
                this.completenessReady = true;
            } finally {
                if (this.shopId === requestedShopId) {
                    this.loading = false;
                }
            }
        },
        async loadStoreInfo(shopId) {
            const nextShopId = shopId || null;

            if (this.shopId !== nextShopId) {
                this.resetCompleteness();
            }

            this.shopId = nextShopId;
            this.storeInfo = emptyStoreInfo();

            if (!nextShopId) {
                this.loading = false;
                return;
            }

            this.loading = true;
            const requestedShopId = nextShopId;

            try {
                const profile = await getStoreInfo(requestedShopId);

                if (this.shopId !== requestedShopId) {
                    return;
                }

                this.storeInfo = profile;
                this.syncStoreInfoComplete();
                this.completenessReady = true;
            } catch {
                if (this.shopId !== requestedShopId) {
                    return;
                }

                this.storeInfo = emptyStoreInfo();
                this.syncStoreInfoComplete();
                this.completenessReady = true;
            } finally {
                if (this.shopId === requestedShopId) {
                    this.loading = false;
                }
            }
        },
        async updateSection(group, sectionId, payload) {
            const shopStore = useShopStore();
            const shopId = this.shopId || shopStore.selectedShop?.id;

            if (group === SELLER_PROFILE_GROUP.StoreInfo) {
                const saved = await persistStoreInfoSection(shopId, sectionId, payload);
                this.storeInfo[sectionId] = saved;
                this.syncStoreInfoComplete();
                return saved;
            }

            const saved = await persistPaymentDeliverySection(shopId, sectionId, payload);
            this.paymentDelivery[sectionId] = saved;
            this.syncPaymentDeliveryComplete();
            return saved;
        },
    },
});
